// Plays a confirmation chime + raises a desktop Notification when an
// alert ticket changes status (e.g. PENDING → ASSIGNED, ASSIGNED → EN_ROUTE).
// The operator may have the dashboard tab in the background — the desktop
// notification ensures they still notice that the patrol is moving through
// the workflow without having to manually refresh or stare at the screen.
//
// Design choices:
// - Reuses the existing `processConfirm` slot from settings-store so the
//   operator can customise the chime in Configuración → Sonidos. We do not
//   create a new slot to avoid bloating the settings UI.
// - We only fire on transitions we deem actionable for the operator:
//     PENDING    → ASSIGNED  (patrol accepted)
//     ASSIGNED   → EN_ROUTE  (patrol moving)
//     EN_ROUTE   → ON_SCENE  (patrol arrived)
//     ON_SCENE   → RESOLVED  (handled in active-alerts list disappearance — separate)
//     any        → CANCELLED (alert was cancelled)
// - Browser Notification permission is requested lazily the first time the
//   operator interacts with the dashboard — never during page load (which
//   browsers block + flag as bad UX).

import type { TicketStatus } from '@asi/shared';
import { useSettingsStore } from '@/stores/settings-store';
import { playBuiltinSound, playCustomAudio } from '@/utils/sound-player';
import { pushAlertToast } from '@/stores/toast-store';

export interface StatusChangeEvent {
  ticketNumber: string;
  clientName?: string;
  patrolName?: string;
  from: TicketStatus;
  to: TicketStatus;
}

const STATUS_LABEL: Record<TicketStatus, string> = {
  PENDING:     'Pendiente',
  ASSIGNED:    'Asignado',
  IN_PROGRESS: 'En proceso',
  EN_ROUTE:    'En camino',
  ON_SCENE:    'En sitio',
  RESOLVED:    'Finalizada',
  CLOSED:      'Cerrada',
  ESCALATED:   'Escalado',
  CANCELLED:   'Cancelado',
};

/** Play the configured processConfirm sound. */
function playStatusChangeChime(): void {
  const s = useSettingsStore.getState().sounds;
  if (!s.masterEnabled || !s.processConfirm.enabled) return;
  if (s.processConfirm.custom?.dataUrl) {
    playCustomAudio(s.processConfirm.custom.dataUrl, s.volume);
  } else {
    playBuiltinSound(s.processConfirm.builtin, s.volume);
  }
}

/** Lazily ask for desktop-notification permission. No longer used — kept
 *  as a no-op so callers do not need to update their imports. */
export async function ensureNotificationPermission(): Promise<NotificationPermission> {
  return 'denied';
}

/** Show an in-app toast notification for the given title + body. */
function showInAppNotification(title: string, body: string): void {
  pushAlertToast({ kind: 'assigned', title, body });
}

/** Public API used by emergency-store after detecting a status diff. */
export function notifyStatusChange(evt: StatusChangeEvent): void {
  // Only notify on transitions the operator should react to. Skip noisy
  // round-trip / no-op transitions.
  if (evt.from === evt.to) return;
  const interesting =
    (evt.from === 'PENDING'  && evt.to === 'ASSIGNED') ||
    (evt.from === 'ASSIGNED' && evt.to === 'EN_ROUTE') ||
    (evt.from === 'EN_ROUTE' && evt.to === 'ON_SCENE') ||
    (evt.to === 'CANCELLED') ||
    (evt.to === 'RESOLVED')   ||
    (evt.to === 'CLOSED');
  if (!interesting) return;

  playStatusChangeChime();

  const who = evt.patrolName ? `${evt.patrolName}` : 'Patrullero';
  const subject = evt.clientName ? `Cliente: ${evt.clientName}` : '';
  const title = `Alerta ${evt.ticketNumber} → ${STATUS_LABEL[evt.to]}`;
  const body = [`${who} • ${STATUS_LABEL[evt.from]} → ${STATUS_LABEL[evt.to]}`, subject]
    .filter(Boolean)
    .join('\n');

  showInAppNotification(title, body);
}
